import React, { useEffect, useMemo, useState } from "react";
import { CompetitionStage, Match, TournamentParticipant } from "../../../types";
import { DAY_MS, WEEKDAYS, getWeekDays, getWeekStart, toLocalDateKey } from "../../../utils/datetime";
import { Button } from "../Button";
import { MatchCardsList } from "./MatchCardsList";

interface EventCalendarPanelProps {
  matches: Match[];
  stages: CompetitionStage[];
  participants: TournamentParticipant[];
}

export const EventCalendarPanel: React.FC<EventCalendarPanelProps> = ({
  matches,
  stages,
  participants,
}) => {
  const stagesById = useMemo(() => new Map(stages.map((stage) => [stage.id, stage])), [stages]);
  const participantsById = useMemo(
    () => new Map(participants.map((participant) => [participant.id, participant])),
    [participants]
  );
  const stageMatches = useMemo(() => new Map(matches.map((match) => [match.id, match])), [matches]);

  const scheduledMatches = useMemo(
    () =>
      matches
        .map((match) => ({
          match,
          scheduledDate: match.scheduled_at ? new Date(match.scheduled_at) : null,
        }))
        .filter((item): item is { match: Match; scheduledDate: Date } => {
          return item.scheduledDate !== null && !Number.isNaN(item.scheduledDate.getTime());
        })
        .sort((a, b) => a.scheduledDate.getTime() - b.scheduledDate.getTime()),
    [matches]
  );

  const matchesByDay = useMemo(() => {
    const dayMap = new Map<string, typeof scheduledMatches>();
    scheduledMatches.forEach((item) => {
      const dayKey = toLocalDateKey(item.scheduledDate);
      dayMap.set(dayKey, [...(dayMap.get(dayKey) ?? []), item]);
    });
    return dayMap;
  }, [scheduledMatches]);

  const firstScheduledDate = useMemo(
    () => scheduledMatches[0]?.scheduledDate ?? new Date(),
    [scheduledMatches]
  );
  const firstScheduledDayKey = useMemo(
    () => scheduledMatches[0] ? toLocalDateKey(scheduledMatches[0].scheduledDate) : "",
    [scheduledMatches]
  );
  const [weekStart, setWeekStart] = useState(() => getWeekStart(firstScheduledDate));
  const [selectedDayKey, setSelectedDayKey] = useState(firstScheduledDayKey);

  useEffect(() => {
    if (!scheduledMatches.length) return;
    if (!matchesByDay.has(selectedDayKey)) {
      setSelectedDayKey(firstScheduledDayKey);
      setWeekStart(getWeekStart(firstScheduledDate));
    }
  }, [firstScheduledDate, firstScheduledDayKey, matchesByDay, scheduledMatches.length, selectedDayKey]);

  const weekDays = useMemo(() => getWeekDays(weekStart), [weekStart]);
  const selectedMatches = useMemo(
    () => (matchesByDay.get(selectedDayKey) ?? []).map((item) => item.match),
    [matchesByDay, selectedDayKey]
  );
  const selectedDate = matchesByDay.get(selectedDayKey)?.[0]?.scheduledDate ?? null;

  const weekRangeLabel = `${weekDays[0].toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
  })} - ${weekDays[6].toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })}`;

  if (scheduledMatches.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6 my-6">
      <section className="bg-white border border-gray-300 rounded shadow-sm p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setWeekStart((current) => new Date(current.getTime() - 7 * DAY_MS))}
          >
            ←
          </Button>
          <h2 className="text-lg font-semibold capitalize text-center">{weekRangeLabel}</h2>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setWeekStart((current) => new Date(current.getTime() + 7 * DAY_MS))}
          >
            →
          </Button>
        </div>

        <div className="grid grid-cols-7 border-l border-t border-gray-300">
          {weekDays.map((day, index) => {
            const dayKey = toLocalDateKey(day);
            const dayMatches = matchesByDay.get(dayKey) ?? [];
            const isSelectable = dayMatches.length > 0;
            const isSelected = selectedDayKey === dayKey;
            return (
              <button
                key={dayKey}
                type="button"
                disabled={!isSelectable}
                onClick={() => setSelectedDayKey(dayKey)}
                className={`border-r border-b border-gray-300 p-2 text-center transition-colors ${
                  isSelected
                    ? "bg-blue-50 text-blue-900"
                    : isSelectable
                      ? "bg-white hover:bg-gray-50 text-gray-900"
                      : "bg-gray-50 text-gray-400 cursor-default"
                }`}
              >
                <div className="text-xs font-semibold">{WEEKDAYS[index]}</div>
                <div className="text-sm font-bold">{day.getDate()}</div>
                {isSelectable ? (
                  <div className="inline-flex min-w-6 justify-center rounded bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-800">
                    {dayMatches.length}
                  </div>
                ) : null}
              </button>
            );
          })}
        </div>
      </section>

      <section className="bg-white border border-gray-300 rounded shadow-sm p-5">
        <h3 className="font-semibold mb-4">
          {selectedDate?.toLocaleDateString("it-IT", {
            weekday: "long",
            day: "2-digit",
            month: "long",
            year: "numeric",
          })}
        </h3>
        <MatchCardsList
          matches={selectedMatches}
          stageMatches={stageMatches}
          participantsById={participantsById}
          participants={participants}
          getSlotLabel={() => "TBD"}
          stagesById={stagesById}
          showStageName
        />
      </section>
    </div>
  );
};
